import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import SettingsLayout from '../../components/SettingsLayout';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { Download, FileJson } from 'lucide-react';
import { API_URL } from '../../lib/apiBase';

const DataExportPage = () => {
  const { currentWorkspaceId, currentWorkspace } = useWorkspace();
  const [downloading, setDownloading] = useState(false);

  const handleExport = async () => {
    setDownloading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/user/export`, {
        params: { workspace_id: currentWorkspaceId },
      });
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const ts = new Date().toISOString().slice(0, 10);
      a.download = `vibesprint-export-${ts}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Export downloaded');
    } catch (_e) {
      toast.error('Export failed — please try again');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <SettingsLayout
      title="Data export"
      description="Download a JSON archive of signals, decisions, roadmap items, and changelog entries for the active workspace."
    >
      <Card className="p-6" data-testid="data-export-card">
        <div className="flex items-center gap-4 mb-4">
          <div className="rounded-md bg-primary/10 p-3">
            <FileJson className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h3 className="text-base font-semibold">Workspace export (JSON)</h3>
            <p className="text-sm text-muted-foreground">
              Bundles this workspace's signals, decisions, roadmap, and changelog history in one portable file.
            </p>
          </div>
        </div>

        <ul className="text-xs text-muted-foreground space-y-1 mb-6 list-disc pl-4">
          <li>Includes only the active workspace</li>
          <li>UTF-8 encoded, human-readable JSON</li>
          <li>Safe to re-import once the migration endpoint ships</li>
          <li>No PII beyond what you've entered yourself</li>
        </ul>

        <Button
          onClick={handleExport}
          disabled={downloading || !currentWorkspaceId}
          className="btn-cta"
          data-testid="data-export-button"
        >
          <Download className="h-4 w-4 mr-1.5" />
          {downloading ? 'Preparing export…' : 'Download JSON'}
        </Button>
      </Card>

      <p className="text-xs text-muted-foreground mt-4">
        Currently exporting workspace: <span className="font-medium text-foreground">{currentWorkspace?.name || 'Demo'}</span>
        {' · '}
        <span className="font-mono">{currentWorkspaceId || 'ws_demo'}</span>
      </p>
    </SettingsLayout>
  );
};

export default DataExportPage;
